import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { theme } from '../config/theme';
import { commonVariants } from '../config/animations';
import { Button } from '../junto-frontend/components/ui';
import Media from './Media/Media';

const SectionWrapper = styled(motion.section)`
  padding: 120px 0;
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 80px;
  padding-right: 80px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 80px 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 120px;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const TextColumn = styled(motion.div)`
  order: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 2;
    text-align: center;
  }
`;

const ImageColumn = styled(motion.div)`
  order: 2;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 1;
  }
`;

const Heading = styled(motion.h2)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: ${theme.typography.primary.largeHeading.fontWeight};
  line-height: ${theme.typography.primary.largeHeading.lineHeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: left;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
  }
  
  em {
    font-family: ${theme.typography.primary.fontFamily};
    font-style: italic;
    color: ${theme.colors.primary.text};
  }
`;

const Description = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: ${theme.typography.secondary.body.fontWeight};
  line-height: ${theme.typography.secondary.body.lineHeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: left;
  max-width: 480px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    text-align: center;
  }
`;


const GalleryContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
  height: 600px;
  
  /* First image takes full left column */
  & > div:first-child {
    grid-column: 1;
    grid-row: 1 / -1;
  }
  
  /* Second and third images stack on the right */
  & > div:nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }
  
  & > div:nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: row;
    gap: 15px;
    height: 250px;
    
    /* Reset grid behavior on mobile */
    & > div:first-child,
    & > div:nth-child(2),
    & > div:nth-child(3) {
      grid-column: unset;
      grid-row: unset;
      flex: 1;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  overflow: hidden;
  border: 1px solid ${theme.shadows.light};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
`;

const MediaWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: block;
`;

const SingleMediaWrapper = styled.div`
  width: 100%;
  height: auto;
  max-height: 600px;
  display: block;
  overflow: hidden;
  
  img, video {
    width: 100%;
    height: auto;
    max-height: 600px;
    object-fit: cover;
    display: block;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-height: 400px;
    
    img, video {
      max-height: 400px;
    }
  }
`;

const CleanSection = ({
  headline,
  description,
  image,
  images,
  buttonText,
  buttonLink,
  onClick,
  galleryLink,
  ...props
}) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  
  const navigate = useNavigate();
  
  const handleImageClick = () => {
    if (galleryLink) {
      navigate(galleryLink);
    }
  };
  
  const [positionImages, setPositionImages] = useState([null, null, null]); // [left, top-right, bottom-right]
  const [nextImageIndex, setNextImageIndex] = useState(0);
  const isGallery = Array.isArray(images) && images.length > 0;
  
  // Initialize with first 3 images
  useEffect(() => {
    if (!isGallery) return;
    
    const now = Date.now();
    const initialImages = [
      { src: images[0], key: `img-0-${now}`, timestamp: now },
      { src: images[1] || images[0], key: `img-1-${now}`, timestamp: now + 1 },
      { src: images[2] || images[0], key: `img-2-${now}`, timestamp: now + 2 }
    ];
    setPositionImages(initialImages);
    setNextImageIndex(3 % images.length);
  }, [isGallery, images]);
  
  useEffect(() => {
    if (!isGallery || images.length <= 3) return;
    
    const interval = setInterval(() => {
      setPositionImages(prev => {
        // Find the position with the oldest image (smallest timestamp)
        let oldestIndex = 0;
        let oldestTimestamp = prev[0]?.timestamp || 0;
        
        for (let i = 1; i < prev.length; i++) {
          if (prev[i]?.timestamp < oldestTimestamp) {
            oldestTimestamp = prev[i].timestamp;
            oldestIndex = i;
          }
        }
        
        // Replace the oldest image with the next image
        const newImages = [...prev];
        newImages[oldestIndex] = {
          src: images[nextImageIndex],
          key: `img-${nextImageIndex}-${Date.now()}`,
          timestamp: Date.now()
        };
        return newImages;
      });
      
      setNextImageIndex(prev => (prev + 1) % images.length);
    }, theme.animations.imageRotationInterval);
    
    return () => clearInterval(interval);
  }, [isGallery, images, nextImageIndex]);

  // Use shared animation variants
  const containerVariants = commonVariants.containerVariants;
  const itemVariants = commonVariants.itemVariants;
  const imageVariants = commonVariants.imageVariants;

  return (
    <SectionWrapper
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      {...props}
    >
      <Grid>
        <TextColumn>
          <Heading 
            variants={itemVariants}
            dangerouslySetInnerHTML={{ __html: headline }}
          />
          <Description 
            variants={itemVariants}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {buttonText && (
            <motion.div variants={itemVariants}>
              <Button 
                as="a"
                href={buttonLink}
                onClick={onClick}
              >
                {buttonText}
              </Button>
            </motion.div>
          )}
        </TextColumn>
        
        <ImageColumn>
          {isGallery ? (
            <GalleryContainer variants={imageVariants}>
              {positionImages.map((imageData, index) => (
                <ImageContainer
                  key={`position-${index}`}
                  variants={imageVariants}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={galleryLink ? commonVariants.buttonHover : {}}
                  transition={{
                    duration: 0.8,
                    ease: 'easeOut',
                  }}
                  $clickable={!!galleryLink}
                  onClick={handleImageClick}
                >
                  <AnimatePresence mode="wait">
                    {imageData && (
                      <MediaWrapper
                        key={imageData.key}
                        as={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Media 
                          src={imageData.src}
                          alt={`${headline} studio ${index + 1}`}
                        />
                      </MediaWrapper>
                    )}
                  </AnimatePresence>
                </ImageContainer>
              ))}
            </GalleryContainer>
          ) : (
            <SingleMediaWrapper 
              as={motion.div}
              variants={imageVariants}
            >
              <Media 
                src={image} 
                alt={headline}
              />
            </SingleMediaWrapper>
          )}
        </ImageColumn>
      </Grid>
    </SectionWrapper>
  );
};

export default CleanSection;